import JSZip from "jszip";

//TODO: later add zip and unzip 3B folder ?? pro no bc not much size savings + longer load time

/**
 * A manager class for manipulating USDZ files (which are uncompressed zip archives).
 * This class allows loading a USDZ from a URL or File object, adding new files
 * to a specific folder within it, and re-zipping the contents.
 */
export class UsdzManager {
    private zip: JSZip | null = null;
    private originalFileName: string = "crystal.3B.usdz"; // not used

    /**
     * Loads a USDZ file from a given URL.
     * @param url The public URL of the .usdz file.
     */
    public async loadFromUrl(url: string): Promise<void> {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.statusText}`);
            }
            const data = await response.arrayBuffer();
            this.zip = await JSZip.loadAsync(data);

            // Extract a filename from the URL for later use
            const urlParts = url.split("/");
            this.originalFileName = urlParts.pop() || this.originalFileName;

            console.log(`USDZ file loaded successfully from URL: ${url}`);
        } catch (error) {
            console.error("Error loading USDZ from URL:", error);
            this.zip = null; // Reset on error
            throw error; // Re-throw to allow caller to handle it
        }
    }

    /**
     * Loads a USDZ file from a File object (e.g., from a file input).
     * @param file The File object representing the .usdz file.
     */
    public async loadFromFile(file: File): Promise<void> {
        if (!file.name.toLowerCase().endsWith(".usdz")) {
            console.warn(
                "Warning: The provided file does not have a .usdz extension."
            );
        }
        try {
            const data = await file.arrayBuffer();
            this.zip = await JSZip.loadAsync(data);
            this.originalFileName = file.name;
            console.log(
                `USDZ file loaded successfully from File: ${file.name}`
            );
        } catch (error) {
            console.error("Error loading USDZ from File:", error);
            this.zip = null; // Reset on error
            throw error;
        }
    }

    /**
     * Adds an array of files to a "3B" folder within the loaded USDZ archive
     * and then re-zips the entire archive.
     *
     * @param newFiles An array of File objects to add to the archive.
     * @returns A Promise that resolves with a Blob of the new .usdz file.
     */
    public async addFilesAndRezip(newFiles: File[]): Promise<Blob> {
        if (!this.zip) {
            throw new Error(
                "No USDZ file has been loaded. Please call loadFromUrl() or loadFromFile() first."
            );
        }

        if (!newFiles || newFiles.length === 0) {
            console.warn(
                "No new files were provided to add. Returning the original archive."
            );
        } else {
            // Get or create the "3B" folder
            const folder3B = this.zip.folder("3B");

            if (!folder3B) {
                // This state should be unreachable as .folder() with a string name always returns an object
                throw new Error(
                    "Failed to create the '3B' directory in the archive."
                );
            }

            console.log(
                `Adding ${newFiles.length} file(s) to the '3B' folder...`
            );

            // Add each new file to the "3B" folder
            // Using Promise.all to handle file reading asynchronously
            await Promise.all(
                newFiles.map(async (file) => {
                    const fileContent = await file.arrayBuffer();
                    folder3B.file(file.name, fileContent);
                    console.log(`- Added ${file.name} to '3B' folder.`);
                })
            );
        }

        console.log("Generating new USDZ file...");

        // Generate the new zip file.
        // USDZ files are specifically *uncompressed* zip archives.
        const newUsdzBlob = await this.zip.generateAsync({
            type: "blob",
            compression: "STORE" // Critical for USDZ format
        });

        console.log("New USDZ file generated successfully as a Blob.");
        return newUsdzBlob;
    }

    /**
     * NEW METHOD
     * Retrieves the contents of the "3B" folder from the archive as an array of File objects.
     * @returns A Promise that resolves with an array of File objects, or an empty array if the folder doesn't exist or is empty.
     */
    public async getFilesFrom3BFolder(): Promise<File[]> {
        if (!this.zip) {
            throw new Error(
                "No USDZ file has been loaded. Please call loadFromUrl() or loadFromFile() first."
            );
        }

        const filesIn3B: JSZip.JSZipObject[] = [];

        // Find all file objects within the "3B" directory.
        // We filter by path prefix and ensure it's not a directory itself.
        this.zip.forEach((relativePath, zipObject) => {
            if (
                relativePath.toLowerCase().startsWith("3b/") &&
                !zipObject.dir
            ) {
                filesIn3B.push(zipObject);
            }
        });

        if (filesIn3B.length === 0) {
            console.log("No files found in the '3B' folder.");
            return [];
        }

        console.log(
            `Found ${filesIn3B.length} file(s) in the '3B' folder. Converting to File objects...`
        );

        // Asynchronously convert each JSZipObject into a browser File object.
        const filePromises = filesIn3B.map(async (zipObject) => {
            const contentBlob = await zipObject.async("blob");

            // Extract the simple file name from the full path (e.g., "3B/info.txt" -> "info.txt")
            const fileName = zipObject.name.substring(
                zipObject.name.indexOf("/") + 1
            );

            // The MIME type isn't stored in the zip, so we let the browser infer it.
            return new File([contentBlob], fileName);
        });

        return Promise.all(filePromises);
    }

    /**
     * Returns the name of the originally loaded file.
     */
    public getOriginalFileName(): string {
        return this.originalFileName;
    }

    /**
     * Checks if a USDZ file is currently loaded in the manager.
     */
    public isLoaded(): boolean {
        return this.zip !== null;
    }
}
